import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as api from './discounts.api';

import type {
  CreateDiscountRequest,
  DiscountListQuery,
  UpdateDiscountRequest,
} from '@utazzo/shared';

const QK = {
  list: (query: DiscountListQuery) => ['discounts', 'list', query] as const,
  detail: (id: string) => ['discounts', 'detail', id] as const,
};

export const useDiscountsList = (query: DiscountListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => api.listDiscounts(query),
    staleTime: 30_000,
  });

export const useCreateDiscount = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateDiscountRequest) => api.createDiscount(payload),
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['discounts', 'list'] });
      toast.success(`Added ${row.name}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add discount'),
  });
};

export const useUpdateDiscount = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateDiscountRequest }) =>
      api.updateDiscount(id, payload),
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['discounts', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(row.id) });
      toast.success('Discount updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update discount'),
  });
};

export const useDeleteDiscount = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteDiscount(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['discounts', 'list'] });
      toast.success('Discount deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete discount'),
  });
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
